import { useQuery } from '@apollo/client';
import { WalletTypeEnum } from '@libs/backend-common';
import { roundDecimal } from '@libs/helpers';
import { HistoryPeriodEnumKeys, RealFiatAssets } from '@libs/types';
import { useMemo } from 'react';
import { getWalletInfoDetailedQuery, WalletInfoDetailed } from 'src/gql';

import { useApiResponse } from 'src/hooks';

export const PIE_CHART_PERCENTAGE_THRESHOLD = 2;
const PIE_CHART_MAX_ASSET_COUNT = 5;

type UseWalletChartAssetsProps = {
  activeDurationKey: HistoryPeriodEnumKeys;
  selectedFiat: RealFiatAssets;
  pieChartMaxAssetCount?: number;
};

export type WalletChartInfo = {
  filtered: WalletInfoDetailed[];
  filteredOut: WalletInfoDetailed[];
  otherCoinsValue: number;
  otherCoinsPercentage: number;
  totalAssetCount?: number;
  loading: boolean;
};

export const useWalletChartAssets = ({
  activeDurationKey,
  selectedFiat,
  pieChartMaxAssetCount = PIE_CHART_MAX_ASSET_COUNT,
}: UseWalletChartAssetsProps): WalletChartInfo => {
  const res = useQuery(getWalletInfoDetailedQuery, {
    variables: {
      record: {
        valueAssetCode: selectedFiat,
        walletType: WalletTypeEnum.NON_ZERO_ASSETS,
      },
      changeDetailsRecord: {
        period: activeDurationKey,
        valueAssetCode: selectedFiat,
      },
    },
  });
  const { response, loading } = useApiResponse(res);

  const { filtered, filteredOut } = useMemo(() => {
    const defaultAssets = {
      filteredOut: [] as WalletInfoDetailed[],
      filtered: [] as WalletInfoDetailed[],
    };
    if (!response?.getWalletInfoDetailed) return defaultAssets;

    return response.getWalletInfoDetailed
      .slice()
      .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
      .reduce((prev, curr) => {
        const percentage = curr.percentage || 0;
        if (
          percentage >= PIE_CHART_PERCENTAGE_THRESHOLD &&
          pieChartMaxAssetCount > prev.filtered.length
        ) {
          return {
            ...prev,
            filtered: [...prev.filtered, curr],
          };
        } else {
          return {
            ...prev,
            filteredOut: [...prev.filteredOut, curr],
          };
        }
      }, defaultAssets);
  }, [response, pieChartMaxAssetCount]);

  const otherCoinsPercentage = useMemo(() => {
    const otherPerc = filteredOut.reduce(
      (prev, curr) => prev + (curr.percentage || 0),
      0
    );
    return roundDecimal(otherPerc, 2);
  }, [filteredOut]);

  const otherCoinsValue = useMemo(
    () => filteredOut.reduce((prev, curr) => prev + +(curr.value || 0), 0),
    [filteredOut]
  );
  const totalAssetCount = response?.getWalletInfoDetailed.length;

  return {
    filtered,
    filteredOut,
    otherCoinsValue,
    otherCoinsPercentage,
    totalAssetCount,
    loading,
  };
};
